import { Environment } from 'environment.js'
import { get, post, put, removeWithPayload } from 'Repository/index.js'
import type { Observable } from 'rxjs'
import type { SavedViz } from '../../View/Component/ConfigurableVisualization/Persistence/SavedViz.js'
import type { IUser } from 'Model/index.js'

/** ------------------------------------------------------------------------
 *  Types
 *  ----------------------------------------------------------------------*/
export type WorkspaceStateResponse = {
  id: string
  projectId?: string | null
  orderId?: string | null
  scopeId?: string | null
  name: string
  comment?: string | null
  state: SavedViz
  isTemplate: boolean
  isShared: boolean
  schemaVersion: string
  createdAt?: string
  modifiedAt?: string
  lastUsedAt?: string
  applicableToTaxas?: string[]
}

export type CreateWorkspaceStateRequest = {
  projectId?: string | null
  orderId?: string | null
  name: string
  comment?: string | null
  state: SavedViz
  isTemplate: boolean
  schemaVersion: string
  applicableToTaxas?: string[] | null
}

export type UpdateWorkspaceStateRequest = {
  name?: string
  comment?: string
  state?: SavedViz
  isTemplate?: boolean
  schemaVersion?: string
  touchAt?: string
  version: string
}

/** ------------------------------------------------------------------------
 *  Endpoints
 *  ----------------------------------------------------------------------*/

/**
 * List all workspace states (views/templates) for a given project or order.
 */
export const listWorkspaceStates = (scopeId: string, user?: IUser): Observable<WorkspaceStateResponse[]> => {
  return get(`${Environment.API_URL}/api/workspace-states/scope/${scopeId}`, {}, user)
}

/**
 * List all globally shared templates (optionally filtered by taxonomy).
 */
export const listSharedTemplates = (taxonomy?: string, user?: IUser): Observable<WorkspaceStateResponse[]> => {
  const query = taxonomy ? `?taxonomy=${encodeURIComponent(taxonomy)}` : ''
  return get(`${Environment.API_URL}/api/workspace-states/templates${query}`, {}, user)
}

/**
 * Retrieve a single workspace state (only if visible to current user).
 */
export const getWorkspaceState = (id: string, user?: IUser): Observable<WorkspaceStateResponse> => {
  return get(`${Environment.API_URL}/api/workspace-states/${id}`, {}, user)
}

/**
 * Create a new workspace state (view or template).
 * Server enforces that `isShared` cannot be set via API.
 */
export const createWorkspaceState = (data: CreateWorkspaceStateRequest, user?: IUser): Observable<WorkspaceStateResponse> => {
  return post(`${Environment.API_URL}/api/workspace-states/`, data, user)
}

export const updateWorkspaceState = (id: string, data: UpdateWorkspaceStateRequest, user?: IUser): Observable<WorkspaceStateResponse> => {
  return put(`${Environment.API_URL}/api/workspace-states/${id}`, data, user)
}

export const deleteWorkspaceState = (id: string, data: { modifiedAt: string }, user?: IUser): Observable<void> => {
  return removeWithPayload(`${Environment.API_URL}/api/workspace-states/${id}`, data, user)
}
